import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useCruises, Cruise, Port } from '../context/CruiseContext';
import { useAuth } from '../context/AuthContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { PlusCircle, X, Loader2, ChevronLeft, ArrowLeft, Car, Plane, Train, Info, FileText, CheckCircle, ChefHat, Wifi, GlassWater, Calendar, Link2, Link2Off, Trash2 } from 'lucide-react';
import { CurrencyIcon } from '../components/CurrencyIcon';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';
import CruiseConflictModal from '../components/CruiseConflictModal';
import CruiseDatePicker from '../components/CruiseDatePicker';
import BreakLinkModal from '../components/BreakLinkModal';
import { checkCruiseConflict } from '../utils/dateUtils';
import { startOfDay, parseISO, format, isValid } from 'date-fns';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { FormSkeleton } from '../components/LoadingSkeleton';
import { toast } from 'react-hot-toast';
import { debug } from '../utils/debug';
import { useItinerary } from '../context/ItineraryContext';
import { formatTimeString } from '../utils/formatUtils';
import { deleteNotificationsForCruise } from '../services/notificationService';

interface TravelDetail {
  id: string;
  type: 'rental-car' | 'train' | 'airplane';
  company?: string;
  reservationNumber?: string;
  departureTime?: string;
  arrivalTime?: string;
  departureLocation?: string;
  arrivalLocation?: string;
  flightNumber?: string;
  trainNumber?: string;
  carType?: string;
  pickupLocation?: string;
  returnLocation?: string;
  pickupTime?: string;
  returnTime?: string;
}

interface CruiseFormData {
  cruiseLine: string;
  shipName: string;
  departureDate: string;
  returnDate: string;
  departurePort: string;
  isGuaranteedCabin: boolean;
  cabinNumber?: string | null;
  cabinType: string;
  bookingNumber?: string | null;
  cruiseCost?: number;
  portTaxesFees?: number;
  additionalCosts?: number;
  isComplimentaryCasinoSailing: boolean;
  notes?: string;
  ports: Port[];
  travelDetails: TravelDetail[];
  diningTime?: string;
  hasDrinkPackage?: boolean;
  hasInternetPackage?: boolean;
  // New post-cruise fields
  gamblingWins?: number;
  gamblingLosses?: number;
  onboardExpenses?: number;
  // Travel agent fields
  travelAgentName?: string;
  travelAgentPhone?: string;
  travelAgentEmail?: string;
}

const EditCruise = () => {
  const { id } = useParams<{ id: string }>();
  const { updateCruise, getCruise, deleteCruise, cruises } = useCruises();
  const { loading: metadataLoading } = useCruiseMetadata();
  const { getItineraryByCompositeKey } = useItinerary();
  const { currentUser, displayPreferences } = useAuth();
  const navigate = useNavigate();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cruise, setCruise] = useState<Cruise | null>(null);
  const [loadingCruise, setLoadingCruise] = useState(true);
  const [showPortsForm, setShowPortsForm] = useState(false);
  const [showTravelForm, setShowTravelForm] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [pendingCruiseData, setPendingCruiseData] = useState<CruiseFormData | null>(null);
  
  // Date conflict modal state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingCruise, setConflictingCruise] = useState<Cruise | null>(null);
  const [pendingFormData, setPendingFormData] = useState<CruiseFormData | null>(null);
  
  // Date picker state
  const [departureDate, setDepartureDate] = useState<Date | null>(null);
  const [returnDate, setReturnDate] = useState<Date | null>(null);

  // ── Itinerary sync state ──
  // null = checking, true = exists, false = gone (stale pointer)
  const [itineraryAvailable, setItineraryAvailable] = useState<boolean | null>(null);
  // Whether ports are currently locked (linked). Starts true if cruise has a key; user can break it in-session.
  const [portsLinked, setPortsLinked] = useState(false);
  // In-session: user broke the link but hasn't saved yet — offer Re-Link
  const [linkBrokenInSession, setLinkBrokenInSession] = useState(false);
  // itineraryKey for this cruise (may be undefined on legacy cruises)
  const [cruiseItineraryKey, setCruiseItineraryKey] = useState<string | undefined>(undefined);
  // BreakLink / Revert modal
  const [showBreakLinkModal, setShowBreakLinkModal] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Refs to track previous values for port reset logic
  const prevDepartureDateRef = useRef<string>('');
  const prevReturnDateRef = useRef<string>('');

  // Helper function to safely parse dates
  const safeParseDate = (dateString: string | null | undefined): Date | undefined => {
    if (!dateString) return undefined;
    try {
      const parsed = parseISO(dateString);
      return isValid(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  };

  const { register, control, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<CruiseFormData>({
    defaultValues: {
      ports: [],
      travelDetails: [],
      isGuaranteedCabin: false,
      isComplimentaryCasinoSailing: false
    }
  });

  const { fields: portFields, append: appendPort, remove: removePort, replace: replacePorts } = useFieldArray({
    control,
    name: "ports"
  });

  const { fields: travelFields, append: appendTravel, remove: removeTravel } = useFieldArray({
    control,
    name: "travelDetails"
  });

  const watchDepartureDate = watch('departureDate');
  const watchReturnDate = watch('returnDate');

  const watchPorts = watch('ports');
  const isGuaranteedCabin = watch('isGuaranteedCabin');
  const isComplimentaryCasinoSailing = watch('isComplimentaryCasinoSailing');

  // Port date reset logic when cruise dates change — only fires when ports are NOT locked
  useEffect(() => {
    if (portsLinked) return; // Locked ports cannot be date-reset
    const currentDepartureDate = watchDepartureDate || '';
    const currentReturnDate = watchReturnDate || '';
    
    // Check if either departure or return date has changed
    const departureDateChanged = currentDepartureDate !== prevDepartureDateRef.current;
    const returnDateChanged = currentReturnDate !== prevReturnDateRef.current;
    
    // Only reset if we have previous values (not initial load) and dates have changed
    if ((departureDateChanged || returnDateChanged) && 
        (prevDepartureDateRef.current || prevReturnDateRef.current)) {
      
      // Check if any ports actually have dates before clearing
      const portsWithDates = watchPorts?.filter((port: Port) => port.date && port.date.trim() !== '') || [];
      
      // Only clear dates and show toast if ports actually have dates
      if (portsWithDates.length > 0) {
        // Clear only the date field for each port, keeping other port information
        const currentPortCount = portFields.length;
        for (let i = 0; i < currentPortCount; i++) {
          setValue(`ports.${i}.date`, '');
        }
        toast.error('Port dates have been cleared due to changes in cruise departure or return dates. Please update the dates for your ports.', {
          duration: 4000,
          icon: <Calendar className="h-10 w-10" />
        });
      }
    }
    
    // Update refs with current values
    prevDepartureDateRef.current = currentDepartureDate;
    prevReturnDateRef.current = currentReturnDate;
  }, [watchDepartureDate, watchReturnDate, setValue, portsLinked]); // watchPorts read inside effect but not in deps to prevent re-runs on port changes
  
  // Update form values when date picker dates change
  useEffect(() => {
    if (departureDate) {
      setValue('departureDate', format(departureDate, 'yyyy-MM-dd'));
    }
  }, [departureDate, setValue]);

  useEffect(() => {
    if (returnDate) {
      setValue('returnDate', format(returnDate, 'yyyy-MM-dd'));
    }
  }, [returnDate, setValue]);

  // Check if cruise departure date is in the past - FIXED VERSION
  const isPastCruise = departureDate ? 
    startOfDay(departureDate) < startOfDay(new Date()) : 
    false;

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Track when data is actually loaded
  useEffect(() => {
    if (!metadataLoading && !loadingCruise) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [metadataLoading, loadingCruise]);

  useEffect(() => {
    const fetchCruise = async () => {
      if (!id || isDeleting) return;
      
      try {
        setLoadingCruise(true);
        const cruiseData = await getCruise(id);
        if (cruiseData) {
          setCruise(cruiseData);
          
          // Format dates for refs
          const formattedDepartureDate = cruiseData.departureDate || '';
          const formattedReturnDate = cruiseData.returnDate || '';
          
          // Set date picker values
          if (cruiseData.departureDate) {
            setDepartureDate(parseISO(cruiseData.departureDate));
          }
          if (cruiseData.returnDate) {
            setReturnDate(parseISO(cruiseData.returnDate));
          }
          
          // Initialize refs with loaded values to prevent reset on initial load
          prevDepartureDateRef.current = formattedDepartureDate;
          prevReturnDateRef.current = formattedReturnDate;
          
          // Reset form with cruise data
          reset({
            cruiseLine: cruiseData.cruiseLine,
            shipName: cruiseData.shipName,
            departureDate: cruiseData.departureDate,
            returnDate: cruiseData.returnDate,
            departurePort: cruiseData.departurePort,
            isGuaranteedCabin: cruiseData.isGuaranteedCabin,
            cabinNumber: cruiseData.cabinNumber,
            cabinType: cruiseData.cabinType,
            bookingNumber: cruiseData.bookingNumber,
            cruiseCost: cruiseData.cruiseCost,
            portTaxesFees: cruiseData.portTaxesFees,
            additionalCosts: cruiseData.additionalCosts,
            isComplimentaryCasinoSailing: cruiseData.isComplimentaryCasinoSailing || false,
            notes: cruiseData.notes,
            ports: cruiseData.ports || [],
            travelDetails: cruiseData.travelDetails || [],
            diningTime: cruiseData.diningTime,
            hasDrinkPackage: cruiseData.hasDrinkPackage || false,
            hasInternetPackage: cruiseData.hasInternetPackage || false,
            gamblingWins: cruiseData.gamblingWins,
            gamblingLosses: cruiseData.gamblingLosses,
            onboardExpenses: cruiseData.onboardExpenses
          });

          // Set up ports form if there are ports
          if (cruiseData.ports && cruiseData.ports.length > 0) {
            setShowPortsForm(true);
          }

          // Set up travel form if there are travel details
          if (cruiseData.travelDetails && cruiseData.travelDetails.length > 0) {
            setShowTravelForm(true);
          }
        }
      } catch (error) {
        console.error('Error fetching cruise:', error);
      } finally {
        setLoadingCruise(false);
      }
    };

    fetchCruise();
  }, [id, getCruise, reset, isDeleting]);

  // Non-blocking itinerary availability check — runs after cruise loads
  useEffect(() => {
    if (!cruise) return;

    if (cruise.itineraryKey) {
      // Cruise has a key — show loading state and do a single getDoc
      setItineraryAvailable(null); // null = checking
      setCruiseItineraryKey(cruise.itineraryKey);
      setPortsLinked(true); // Assume locked until we know it's stale
      
      const checkItinerary = async () => {
        try {
          const itinRef = doc(db, 'itineraries', cruise.itineraryKey!);
          const snap = await getDoc(itinRef);
          setItineraryAvailable(snap.exists());
          // Ports remain locked regardless of stale/live (user still sees their data)
        } catch (err) {
          debug.warn('[EditCruise] Failed to check itinerary availability:', err);
          setItineraryAvailable(false); // Treat as stale on error
        }
      };
      
      checkItinerary();
    } else {
      // Unlinked cruise — check if official itinerary exists for its line/ship/dates
      const key = `${cruise.cruiseLine}_${cruise.shipName}_${cruise.departureDate}_${cruise.returnDate}`;
      const checkUnlinkedAvailability = async () => {
        try {
          const itinRef = doc(db, 'itineraries', key);
          const snap = await getDoc(itinRef);
          setItineraryAvailable(snap.exists());
        } catch (err) {
          debug.warn('[EditCruise] Failed to check official itinerary availability:', err);
          setItineraryAvailable(false);
        }
      };

      checkUnlinkedAvailability();
    }
  }, [cruise?.itineraryKey, cruise?.cruiseLine, cruise?.shipName, cruise?.departureDate, cruise?.returnDate]);

  // Handler: User clicks a locked port field → open the break link modal
  const handleLockedPortClick = () => {
    setShowBreakLinkModal(true);
  };

  // Handler: Break link (or Remove Sync Status for stale)
  const handleBreakLink = () => {
    setPortsLinked(false);
    setLinkBrokenInSession(true);
    setCruiseItineraryKey(undefined); // Will be cleared from Firestore on save
    setShowBreakLinkModal(false);
    toast.success('Sync link removed. Ports are now editable.');
  };

  // Handler: Revert to official itinerary
  const handleRevert = async () => {
    if (!cruise?.itineraryKey) return;
    setIsReverting(true);
    try {
      // Parse the composite key: CruiseLine_ShipName_sailingDate_returnDate
      const parts = cruise.itineraryKey.split('_');
      // cruiseLine and shipName may have underscores, but dates are always last two segments
      const returnDatePart = parts[parts.length - 1];
      const sailingDatePart = parts[parts.length - 2];
      const compositeKey = `${sailingDatePart}_${returnDatePart}`;
      const cruiseLine = parts.slice(0, -3).join('_'); // everything before shipName
      const shipName = parts[parts.length - 3];
      
      const itinerary = await getItineraryByCompositeKey(cruiseLine, shipName, compositeKey);
      if (itinerary && itinerary.ports && itinerary.ports.length > 0) {
        // Overwrite scheduled fields (name, date, arrivalTime, departureTime) but keep excursion notes
        const currentPorts = watch('ports');
        const updatedPorts = itinerary.ports.map((itinPort, idx) => {
          const existing = currentPorts?.[idx];
          return {
            id: existing?.id || uuidv4(),
            name: itinPort.name,
            date: itinPort.date,
            arrivalTime: itinPort.arrivalTime || '',
            departureTime: itinPort.departureTime || '',
            excursion: existing?.excursion || '', // Preserve user's excursion notes
            fromItinerary: true,
          };
        });
        // Replace all port fields
        replacePorts(updatedPorts);
        toast.success('Ports reverted to official itinerary. Excursion notes preserved.');
      } else {
        toast.error('Could not load the official itinerary. Please try again.');
      }
    } catch (err) {
      console.error('[EditCruise] Revert failed:', err);
      toast.error('Failed to revert to official itinerary.');
    } finally {
      setIsReverting(false);
      setShowBreakLinkModal(false);
    }
  };

  // Handler: Link to Official Itinerary (State C — manual cruise, no key yet)
  const handleLinkToOfficial = async () => {
    if (!cruise) return;
    const currentLine = watch('cruiseLine') || cruise.cruiseLine;
    const currentShip = watch('shipName') || cruise.shipName;
    const currentDep = watch('departureDate') || cruise.departureDate;
    const currentRet = watch('returnDate') || cruise.returnDate;
    const key = `${currentLine}_${currentShip}_${currentDep}_${currentRet}`;
    try {
      const snap = await getDoc(doc(db, 'itineraries', key));
      if (snap.exists()) {
        const itinData = snap.data();
        setCruiseItineraryKey(key);
        setPortsLinked(true);
        setLinkBrokenInSession(false);
        setItineraryAvailable(true);

        if (itinData?.ports && Array.isArray(itinData.ports) && itinData.ports.length > 0) {
          const currentPorts = watch('ports');
          const updatedPorts = itinData.ports.map((itinPort: any, idx: number) => {
            const existing = currentPorts?.[idx];
            return {
              id: existing?.id || uuidv4(),
              name: itinPort.name,
              date: itinPort.date,
              arrivalTime: itinPort.arrivalTime || '',
              departureTime: itinPort.departureTime || '',
              excursion: existing?.excursion || '', // Preserve user's excursion notes
              fromItinerary: true,
            };
          });
          replacePorts(updatedPorts);
          setShowPortsForm(true);
        }

        // Clean up any itinerary-available notification for this cruise
        if (currentUser?.uid && id) {
          deleteNotificationsForCruise(currentUser.uid, id, 'itinerary-available').catch((err) =>
            console.error('Failed to clear itinerary-available notifications:', err)
          );
        }

        toast.success('Cruise linked to official itinerary! Ports and times synchronized.');
      } else {
        toast('No official itinerary found for this sailing.');
      }
    } catch (err) {
      console.error('[EditCruise] Link check failed:', err);
      toast.error('Could not check for an official itinerary. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      setIsDeleting(true);
      setDeleteLoading(true);
      setDeleteError(null);
      await deleteCruise(id);
      if (currentUser?.uid && id) {
        deleteNotificationsForCruise(currentUser.uid, id).catch((err) =>
          console.error('Failed to clear notifications for deleted cruise:', err)
        );
      }
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error deleting cruise:', error);
      setIsDeleting(false);
      if (error.message === 'permission-denied' || error.code === 'permission-denied') {
        setDeleteError('You do not have permission to delete this cruise.');
      } else {
        setDeleteError('An error occurred while deleting the cruise. Please try again.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const onSubmit = async (data: CruiseFormData) => {
    if (!id) return;

    const submissionData = {
      ...data,
      cabinNumber: data.isGuaranteedCabin ? null : data.cabinNumber
    };

    // Check for date conflicts before proceeding (exclude current cruise)
    const conflictingCruise = checkCruiseConflict({
      departureDate: submissionData.departureDate,
      returnDate: submissionData.returnDate,
      existingCruises: cruises,
      excludeCruiseId: id
    });

    if (conflictingCruise) {
      // Show conflict modal
      setConflictingCruise(conflictingCruise);
      setPendingFormData(submissionData);
      setShowConflictModal(true);
      return;
    }

    // No conflict, proceed with normal flow
    await proceedWithSave(submissionData);
  };

  const proceedWithSave = async (data: CruiseFormData) => {
    // Check if this is a past cruise
    if (isPastCruise && !cruise?.completed) {
      // Store the data and show completion confirmation modal
      setPendingCruiseData(data);
      setShowCompletionModal(true);
    } else {
      // For future cruises or already completed cruises, save directly
      await saveCruise(data, cruise?.completed || false);
    }
  };

  const saveCruise = async (data: CruiseFormData, completed: boolean) => {
    if (!id) return;

    try {
      setIsLoading(true);
      const cruiseData: any = {
        ...data,
        completed
      };
      // Preserve or clear the itineraryKey based on current link state
      if (cruiseItineraryKey) {
        cruiseData.itineraryKey = cruiseItineraryKey;
        cruiseData.linkBroken = false;
        if (currentUser?.uid && id) {
          deleteNotificationsForCruise(currentUser.uid, id, 'itinerary-available').catch((err) =>
            console.error('Failed to clear itinerary-available notifications:', err)
          );
        }
      } else {
        // Link was broken — remove itineraryKey from Firestore, mark linkBroken, and save previous key
        cruiseData.itineraryKey = null;
        cruiseData.linkBroken = true;
        if (cruise?.itineraryKey) {
          cruiseData.previousItineraryKey = cruise.itineraryKey;
        }
        cruiseData.unlinkedAt = Date.now();
      }
      await updateCruise(id, cruiseData);
      // Clear the history stack up to the dashboard first
      navigate('/dashboard', { replace: true });
      // Then immediately navigate to the updated cruise details page
      // This creates a clean history: Dashboard -> CruiseDetails
      navigate(`/cruise/${id}`);
    } catch (error) {
      console.error('Error updating cruise:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConflictConfirm = () => {
    // User chose to continue despite conflict
    setShowConflictModal(false);
    if (pendingFormData) {
      proceedWithSave(pendingFormData);
    }
    setConflictingCruise(null);
    setPendingFormData(null);
  };

  const handleConflictCancel = () => {
    // User chose to cancel and review dates
    setShowConflictModal(false);
    setConflictingCruise(null);
    setPendingFormData(null);
  };

  const handleCompletionConfirm = async (completed: boolean) => {
    if (!pendingCruiseData) return;
    
    setShowCompletionModal(false);
    await saveCruise(pendingCruiseData, completed);
    setPendingCruiseData(null);
  };

  const handleCompletionCancel = () => {
    setShowCompletionModal(false);
    setPendingCruiseData(null);
  };

  const handleAddPort = () => {
    appendPort({ id: uuidv4(), name: '', date: '', arrivalTime: '', departureTime: '', excursion: '' });
    setShowPortsForm(true);
  };

  const handleAddTravel = (type: 'rental-car' | 'train' | 'airplane') => {
    appendTravel({ id: uuidv4(), type });
    setShowTravelForm(true);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const renderTravelFields = (field: TravelDetail, index: number) => {
    switch (field.type) {
      case 'airplane':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Airline</label>
              <input
                {...register(`travelDetails.${index}.company`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Delta Airlines"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flight Number</label>
              <input
                {...register(`travelDetails.${index}.flightNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="DL1234"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Airport</label>
              <input
                {...register(`travelDetails.${index}.departureLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="JFK"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Airport</label>
              <input
                {...register(`travelDetails.${index}.arrivalLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="MIA"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.departureTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.arrivalTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Number</label>
              <input
                {...register(`travelDetails.${index}.reservationNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABCD123"
              />
            </div>
          </div>
        );

      case 'train':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Company</label>
              <input
                {...register(`travelDetails.${index}.company`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Amtrak"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Number</label>
              <input
                {...register(`travelDetails.${index}.trainNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="123"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Station</label>
              <input
                {...register(`travelDetails.${index}.departureLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Penn Station"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Station</label>
              <input
                {...register(`travelDetails.${index}.arrivalLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Union Station"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.departureTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.arrivalTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Number</label>
              <input
                {...register(`travelDetails.${index}.reservationNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABCD123"
              />
            </div>
          </div>
        );

      case 'rental-car':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rental Company</label>
              <input
                {...register(`travelDetails.${index}.company`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Enterprise"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Type</label>
              <input
                {...register(`travelDetails.${index}.carType`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="SUV, Sedan, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
              <input
                {...register(`travelDetails.${index}.pickupLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Airport Location"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Location</label>
              <input
                {...register(`travelDetails.${index}.returnLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Port Location"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.pickupTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.returnTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Number</label>
              <input
                {...register(`travelDetails.${index}.reservationNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABCD123"
              />
            </div>
          </div>
        );
    }
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return (
      <FormSkeleton />
    );
  }

  if (!cruise) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cruise Not Found</h2>
        <p className="text-gray-600 mb-4">The cruise you're trying to edit doesn't exist or you don't have access to it.</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition duration-200 font-medium"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  return (
    <motion.div 
      className="max-w-3xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Date Conflict Modal */}
      {showConflictModal && conflictingCruise && pendingFormData && (
        <CruiseConflictModal
          isOpen={showConflictModal}
          onClose={handleConflictCancel}
          onConfirm={handleConflictConfirm}
          conflictingCruise={conflictingCruise}
          newCruiseDates={{
            departureDate: pendingFormData.departureDate,
            returnDate: pendingFormData.returnDate,
            shipName: pendingFormData.shipName,
            cruiseLine: pendingFormData.cruiseLine
          }}
        />
      )}

      {/* Completion Confirmation Modal - Using Portal */}
      {showCompletionModal && pendingCruiseData && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 text-blue-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-800">Cruise Completion</h2>
              </div>
              <button
                onClick={handleCompletionCancel}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
            <p className="text-gray-600">
              The cruise you're editing has a departure date in the past. Have you already completed this cruise?
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm">
                <p className="font-medium text-blue-900">{pendingCruiseData.shipName}</p>
                <p className="text-blue-700">{pendingCruiseData.cruiseLine}</p>
                <p className="text-blue-600">
                  {new Date(pendingCruiseData.departureDate).toLocaleDateString()} - {new Date(pendingCruiseData.returnDate).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="space-y-3 pt-4">
              <button
                onClick={() => handleCompletionConfirm(true)}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Yes, Mark as Completed
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleCompletionConfirm(false)}
                disabled={isLoading}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              >
                No, Save as Planned
              </button>
              
              <button
                onClick={handleCompletionCancel}
                disabled={isLoading}
                className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 transition duration-200"
              >
                Go Back to Edit
              </button>
            </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <motion.div 
        className="mb-6 flex items-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <button 
          onClick={() => navigate(-1)}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Edit Cruise</h1>
          <p className="text-gray-600 mt-1">Update your cruise vacation details</p>
        </div>
      </motion.div>

      <motion.div 
        className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="flex">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <p className="text-sm text-blue-700">
            Fields marked with an asterisk (*) are required
          </p>
        </div>
      </motion.div>

      <motion.form 
        onSubmit={handleSubmit(onSubmit)} 
        className="bg-white rounded-lg shadow-md p-6"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <div className="mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-md border border-gray-100">
          <Info className="h-4 w-4 inline-block text-blue-500 mr-1.5 -mt-0.5" />
          If your itinerary or ship is changing, delete this cruise and add a new one.
        </div>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={item}
        >
          {/* ── Cruise Line (locked display) ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cruise Line
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center gap-2">
              <span>{cruise?.cruiseLine || '—'}</span>
            </div>
          </div>

          {/* ── Ship Name (locked display) ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ship Name
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center gap-2">
              <span>{cruise?.shipName || '—'}</span>
            </div>
          </div>

          {/* ── Departure Date (locked display) ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Departure Date
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span>{cruise?.departureDate ? format(parseISO(cruise.departureDate), 'MMM d, yyyy') : '—'}</span>
            </div>
          </div>

          {/* ── Return Date (locked display) ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Date
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span>{cruise?.returnDate ? format(parseISO(cruise.returnDate), 'MMM d, yyyy') : '—'}</span>
            </div>
          </div>

          {/* ── Departure Port (locked display) ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Departure Port
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center gap-2">
              <span>{cruise?.departurePort || '—'}</span>
              <span className="ml-auto text-xs text-gray-400 italic"></span>
            </div>
          </div>

          <div>
            <label htmlFor="cabinType" className="block text-sm font-medium text-gray-700 mb-1">
              Cabin Type
            </label>
            <select
              id="cabinType"
              {...register('cabinType')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                errors.cabinType ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
              }`}
            >
              <option value="">Select cabin type</option>
              <option value="Interior">Interior</option>
              <option value="Ocean View">Ocean View</option>
              <option value="Balcony">Balcony</option>
              <option value="Suite">Suite</option>
            </select>
            {errors.cabinType && (
              <p className="mt-1 text-sm text-red-600">{errors.cabinType.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isGuaranteedCabin"
                {...register('isGuaranteedCabin')}
                className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
              />
              <label htmlFor="isGuaranteedCabin" className="ml-2 block text-sm text-gray-700">
                This is a guaranteed cabin (cabin number will be assigned later)
              </label>
            </div>

            {!isGuaranteedCabin && (
              <div className="mb-6">
                <label htmlFor="cabinNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Cabin Number
                </label>
                <input
                  id="cabinNumber"
                  {...register('cabinNumber')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                    errors.cabinNumber ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
                  }`}
                  placeholder="8245"
                />
                {errors.cabinNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.cabinNumber.message}</p>
                )}
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="bookingNumber" className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Booking Number
                </div>
              </label>
              <input
                id="bookingNumber"
                {...register('bookingNumber')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABC123456"
              />
              <p className="mt-1 text-sm text-gray-500">
                Your cruise line booking confirmation number
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="diningTime" className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <ChefHat className="h-4 w-4 mr-1" />
                  Dining Time
                </div>
              </label>
              <select
                id="diningTime"
                {...register('diningTime')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              >
                <option value="">Select dining time</option>
                <option value="Early">Early</option>
                <option value="Late">Late</option>
                <option value="Flexible">Flexible</option>
                <option value="Other">Other</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Your preferred dining time for the main dining room
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Packages
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasDrinkPackage"
                    {...register('hasDrinkPackage')}
                    className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasDrinkPackage" className="ml-2 flex items-center text-sm text-gray-700">
                    <GlassWater className="h-4 w-4 mr-2 text-blue-500" />
                    Drink Package
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasInternetPackage"
                    {...register('hasInternetPackage')}
                    className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasInternetPackage" className="ml-2 flex items-center text-sm text-gray-700">
                    <Wifi className="h-4 w-4 mr-2 text-purple-500" />
                    Internet Package
                  </label>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Select if you have purchased these packages for this cruise
              </p>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isComplimentaryCasinoSailing"
                  {...register('isComplimentaryCasinoSailing')}
                  className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                />
                <label htmlFor="isComplimentaryCasinoSailing" className="ml-2 block text-sm text-gray-700">
                  This cruise is a complimentary casino sailing
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cruiseCost" className="block text-sm font-medium text-gray-700 mb-1">
                    {isComplimentaryCasinoSailing ? 'Complimentary Cruise Value' : 'Cruise Cost'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="cruiseCost"
                      {...register('cruiseCost')}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="portTaxesFees" className="block text-sm font-medium text-gray-700 mb-1">
                    Port Taxes and Fees
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="portTaxesFees"
                      {...register('portTaxesFees')}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="additionalCosts" className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Cruise Costs (Upgrades, Deposits, Drink Package, etc)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="additionalCosts"
                      {...register('additionalCosts')}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Only show post-cruise fields if departure date is in the past */}
              {isPastCruise && (
                <>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Post-Cruise Details</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Since this cruise is in the past, you can add details about your actual expenses.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="onboardExpenses" className="block text-sm font-medium text-gray-700 mb-1">
                        Onboard Expenses
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          id="onboardExpenses"
                          {...register('onboardExpenses')}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        Spa, specialty dining, drinks, shopping, etc.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="gamblingWins" className="block text-sm font-medium text-gray-700 mb-1">
                        Gambling Wins
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          id="gamblingWins"
                          {...register('gamblingWins')}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="gamblingLosses" className="block text-sm font-medium text-gray-700 mb-1">
                        Gambling Losses
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          id="gamblingLosses"
                          {...register('gamblingLosses')}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Travel Agent Information */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Travel Agent Information</h3>
              <p className="text-sm text-gray-600 mb-4">
                Optional: Add your travel agent's contact information for easy reference.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="travelAgentName" className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Name
                  </label>
                  <input
                    type="text"
                    id="travelAgentName"
                    {...register('travelAgentName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                    placeholder="Agent's full name"
                  />
                </div>

                <div>
                  <label htmlFor="travelAgentPhone" className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Phone
                  </label>
                  <input
                    type="tel"
                    id="travelAgentPhone"
                    {...register('travelAgentPhone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="travelAgentEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Email
                  </label>
                  <input
                    type="email"
                    id="travelAgentEmail"
                    {...register('travelAgentEmail')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                    placeholder="agent@travelagency.com"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="border-t border-gray-200 pt-6 mt-6"
          variants={item}
        >
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
            placeholder="Any additional information about your cruise..."
          />
        </motion.div>

        <motion.div 
          className="mt-8"
          variants={item}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Travel Details</h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleAddTravel('airplane')}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <Plane className="h-4 w-4 mr-1" />
                Add Flight
              </button>
              <button
                type="button"
                onClick={() => handleAddTravel('train')}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <Train className="h-4 w-4 mr-1" />
                Add Train
              </button>
              <button
                type="button"
                onClick={() => handleAddTravel('rental-car')}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <Car className="h-4 w-4 mr-1" />
                Add Car
              </button>
            </div>
          </div>

          {showTravelForm && travelFields.length > 0 && (
            <div className="space-y-6">
              {travelFields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-4 border border-gray-200 rounded-md relative"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {field.type === 'airplane' && <Plane className="h-5 w-5 text-blue-500 mr-2" />}
                      {field.type === 'train' && <Train className="h-5 w-5 text-purple-500 mr-2" />}
                      {field.type === 'rental-car' && <Car className="h-5 w-5 text-green-500 mr-2" />}
                      <h4 className="font-medium text-gray-900">
                        {field.type === 'airplane' && 'Flight Details'}
                        {field.type === 'train' && 'Train Details'}
                        {field.type === 'rental-car' && 'Rental Car Details'}
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        removeTravel(index);
                        if (travelFields.length === 1) setShowTravelForm(false);
                      }}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  {renderTravelFields(field, index)}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* ── Ports of Call + Itinerary Sync section ── */}
        <motion.div variants={item} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">

          {/* ── Itinerary Sync Status Badge ── Only shown when cruise has an itineraryKey or link was recently broken ── */}
          {(cruiseItineraryKey || linkBrokenInSession) && (
            <div className="mt-6 p-4 rounded-xl border flex items-start gap-3"
              style={{
                borderColor: portsLinked
                  ? (itineraryAvailable === null ? '#d1d5db' : itineraryAvailable ? '#86efac' : '#fcd34d')
                  : '#e5e7eb',
                backgroundColor: portsLinked
                  ? (itineraryAvailable === null ? '#f9fafb' : itineraryAvailable ? '#f0fdf4' : '#fffbeb')
                  : '#f9fafb',
              }}
            >
              {/* Icon */}
              <div className="flex-shrink-0 mt-0.5">
                {portsLinked && itineraryAvailable === null && <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />}
                {portsLinked && itineraryAvailable === true && <Link2 className="h-4 w-4 text-green-600" />}
                {portsLinked && itineraryAvailable === false && <Link2Off className="h-4 w-4 text-amber-500" />}
                {!portsLinked && <Link2Off className="h-4 w-4 text-gray-400" />}
              </div>
              {/* Text */}
              <div className="flex-1 min-w-0">
                {portsLinked && itineraryAvailable === null && (
                  <p className="text-sm text-gray-500">Checking official itinerary…</p>
                )}
                {portsLinked && itineraryAvailable === true && (
                  <>
                    <p className="text-sm font-semibold text-green-800">Synced with Official Itinerary</p>
                    <p className="text-xs text-green-700 mt-0.5">Port names and times are locked. Tap a port field to disconnect synchronization and edit manually.</p>
                  </>
                )}
                {portsLinked && itineraryAvailable === false && (
                  <>
                    <p className="text-sm font-semibold text-amber-800">Itinerary Source No Longer Available</p>
                    <p className="text-xs text-amber-700 mt-0.5">Your port data is intact. Tap a port field to remove sync status and edit freely.</p>
                  </>
                )}
                {!portsLinked && linkBrokenInSession && (
                  <div className="flex w-full items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Sync Removed — Ports Now Editable</p>
                      <p className="text-xs text-gray-500 mt-0.5">Your port schedule is no longer locked to the official itinerary.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleLinkToOfficial}
                      className="text-xs px-3 py-1.5 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium transition-colors flex-shrink-0"
                    >
                      Re-link to Official
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* State C: No key, no session break — unlinked cruise, offer to link */}
          {!cruiseItineraryKey && !linkBrokenInSession && (
            <div className={`mt-6 p-4 rounded-xl border flex items-center gap-3 transition-colors ${
              itineraryAvailable
                ? 'border-teal-300 bg-teal-50/80 shadow-sm'
                : 'border-gray-200 bg-gray-50'
            }`}>
              {itineraryAvailable ? (
                <Link2 className="h-5 w-5 text-teal-600 flex-shrink-0" />
              ) : (
                <Link2Off className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}
              <div className="flex-1">
                {itineraryAvailable ? (
                  <>
                    <p className="text-sm font-semibold text-teal-900">Official Itinerary Available!</p>
                    <p className="text-xs text-teal-700 mt-0.5">An official itinerary matches this sailing. Tap to link and sync port schedule.</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-600">Not linked to an official itinerary</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleLinkToOfficial}
                className={`text-xs px-3.5 py-1.5 rounded-lg font-medium transition-colors flex-shrink-0 ${
                  itineraryAvailable
                    ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-sm'
                    : 'border border-blue-300 text-blue-700 hover:bg-blue-50'
                }`}
              >
                Link to Official
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 mt-8">
            <h3 className="text-lg font-semibold text-gray-800">Ports of Call</h3>
            {!portsLinked && (
              <button
                type="button"
                onClick={handleAddPort}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <PlusCircle className="h-4 w-4 mr-1" />
                Add Port
              </button>
            )}
          </div>

          {showPortsForm && portFields.length > 0 && (
            <div className="space-y-4">
              {portFields.map((field, index) => {
                const isLocked = portsLinked; // Port is locked when linked to itinerary
                return (
                  <div
                    key={field.id}
                    className={`p-4 border rounded-md relative ${
                      isLocked ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'
                    }`}
                  >
                    {/* Lock icon / Remove button */}
                    {isLocked ? (
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-blue-100/80 border border-blue-200 text-blue-800 text-xs px-2.5 py-1 rounded-full font-medium">
                        <Link2 className="h-3 w-3 text-blue-600" />
                        <span>Synced</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          removePort(index);
                          if (portFields.length === 1) setShowPortsForm(false);
                        }}
                        className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Port Name */}
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Port Name</label>
                        {isLocked ? (
                          <button
                            type="button"
                            onClick={handleLockedPortClick}
                            className="w-full text-left px-3 py-2 border border-blue-200 rounded-md bg-blue-50 text-gray-700 cursor-pointer hover:bg-blue-100 transition-colors"
                          >
                            {watch(`ports.${index}.name`) || '—'}
                          </button>
                        ) : (
                          <input
                            {...register(`ports.${index}.name`)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                            placeholder="Nassau, Bahamas"
                          />
                        )}
                      </div>

                      {/* Date */}
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        {isLocked ? (
                          <button
                            type="button"
                            onClick={handleLockedPortClick}
                            className="w-full text-left px-3 py-2 border border-blue-200 rounded-md bg-blue-50 text-gray-700 cursor-pointer hover:bg-blue-100 transition-colors"
                          >
                            {watch(`ports.${index}.date`)
                              ? format(parseISO(watch(`ports.${index}.date`)), 'MMM d, yyyy')
                              : '—'}
                          </button>
                        ) : (
                          <>
                            <CruiseDatePicker
                              label=""
                              value={watch(`ports.${index}.date`) ? (safeParseDate(watch(`ports.${index}.date`)) || null) : null}
                              onChange={(date: Date) => {
                                debug.log('Port date onChange called:', { index, date });
                                setValue(`ports.${index}.date`, format(date, 'yyyy-MM-dd'));
                              }}
                              minDate={safeParseDate(watchDepartureDate)}
                              maxDate={safeParseDate(watchReturnDate)}
                              activeStartDate={safeParseDate(watchDepartureDate)}
                              disabled={!watchDepartureDate || !watchReturnDate}
                              placeholder="Select port date"
                              error={errors.ports?.[index]?.date?.message}
                              required={false}
                            />
                            <input
                              type="hidden"
                              {...register(`ports.${index}.date`, {
                                validate: value => {
                                  if (!value || !watchDepartureDate || !watchReturnDate) return true;
                                  const portDate = new Date(value);
                                  const departure = new Date(watchDepartureDate);
                                  const returnD = new Date(watchReturnDate);
                                  if (portDate < departure) return 'Port date cannot be before cruise departure date';
                                  if (portDate > returnD) return 'Port date cannot be after cruise return date';
                                  return true;
                                }
                              })}
                            />
                            {errors.ports?.[index]?.date && (
                              <p className="mt-1 text-sm text-red-600">{errors.ports[index]?.date?.message}</p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Arrival Time */}
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
                        {isLocked ? (
                          <button
                            type="button"
                            onClick={handleLockedPortClick}
                            className="w-full text-left px-3 py-2 border border-blue-200 rounded-md bg-blue-50 text-gray-700 cursor-pointer hover:bg-blue-100 transition-colors"
                          >
                            {watch(`ports.${index}.arrivalTime`) 
                              ? formatTimeString(watch(`ports.${index}.arrivalTime`), displayPreferences) 
                              : '—'}
                          </button>
                        ) : (
                          <input
                            type="time"
                            {...register(`ports.${index}.arrivalTime`)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          />
                        )}
                      </div>

                      {/* Departure Time */}
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
                        {isLocked ? (
                          <button
                            type="button"
                            onClick={handleLockedPortClick}
                            className="w-full text-left px-3 py-2 border border-blue-200 rounded-md bg-blue-50 text-gray-700 cursor-pointer hover:bg-blue-100 transition-colors"
                          >
                            {watch(`ports.${index}.departureTime`) 
                              ? formatTimeString(watch(`ports.${index}.departureTime`), displayPreferences) 
                              : '—'}
                          </button>
                        ) : (
                          <input
                            type="time"
                            {...register(`ports.${index}.departureTime`)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          />
                        )}
                      </div>

                      {/* Excursion — always editable */}
                      <div className="min-w-0 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Excursion</label>
                        <input
                          {...register(`ports.${index}.excursion`)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="Beach day, snorkeling tour, etc."
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        <motion.div
          className="mt-8 grid grid-cols-2 gap-3 sm:flex sm:items-center sm:justify-between"
          variants={item}
        >
          <button
            type="button"
            onClick={() => setDeleteModal(true)}
            className="order-3 col-span-1 flex items-center justify-center px-4 py-2 border border-red-200 text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition duration-200 w-full sm:w-auto sm:order-1"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
          
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="order-2 col-span-1 flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 text-center w-full sm:w-auto sm:order-2 sm:ml-auto sm:mr-3"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isLoading}
            className="order-1 col-span-2 flex items-center justify-center px-6 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium whitespace-nowrap w-full sm:w-auto sm:order-3"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <CustomCruiseIcon size={34} className="mr-2" />
            )}
            {isLoading ? 'Saving...' : 'Update Cruise'}
          </button>
        </motion.div>
        {/* BreakLink Modal */}
        <BreakLinkModal
          isOpen={showBreakLinkModal}
          itineraryAvailable={itineraryAvailable === true}
          onBreakLink={handleBreakLink}
          onRevert={handleRevert}
          onCancel={() => setShowBreakLinkModal(false)}
          isReverting={isReverting}
        />

        {/* Delete Confirmation Modal - Using Portal */}
        {deleteModal && createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => !deleteLoading && setDeleteModal(false)}
            ></div>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative z-10 animate-fade-in-up">
              <h2 className="text-xl font-semibold text-gray-800">Delete Cruise</h2>
              <button 
                type="button"
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => {
                  setDeleteModal(false);
                  setDeleteError(null);
                }}
                disabled={deleteLoading}
              >
                <X className="h-5 w-5" />
              </button>
              
              <div className="mt-4">
                <p className="text-gray-600">
                  Are you sure you want to delete this cruise? This action cannot be undone.
                </p>
                {deleteError && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-sm text-red-800">{deleteError}</p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setDeleteModal(false);
                    setDeleteError(null);
                  }}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center justify-center min-w-[100px]"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </motion.form>
    </motion.div>
  );
};

export default EditCruise;